import { Text, Title } from "@clipboard-health/ui-components";
import { Box } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { Button } from "@src/appV2/redesign/components/Button";
import { PageFooterWrapper } from "@src/appV2/redesign/components/PageFooterWrapper";
import { SwipeableViewsWithDots } from "@src/appV2/redesign/components/SwipeableViewsWithDots";
import { useCallback, useState } from "react";

import { ONBOARDING_STAGES } from "../../util/constants";
import { EducationIntroSlide } from "../components/EducationIntroSlide";
import { type OnboardingStepProps } from "../OnboardingStepWrapper";
import { InstantBookSlide } from "./components/InstantBookSlide";
import { InstantPaySlide } from "./components/InstantPaySlide";
import { UploadDocumentsSlide } from "./components/UploadDocumentsSlide";
import { type EducationSlide } from "./types";

const SLIDES: EducationSlide[] = [
  {
    id: "intro",
    title: "Welcome to Clipboard",
    subtitle: "Take a tour of our shifts marketplace",
  },
  {
    id: "upload-documents",
    title: "Upload your documents to access shift bookings",
    subtitle: "Your document is approved",
    content: "You can now proceed to booking shifts!",
  },
  {
    id: "instant-book",
    title: "Claim individual shifts instantly to build your own schedule",
  },
  {
    id: "instant-pay",
    title: "Link your bank account to get paid instantly after every shift",
  },
];

function SlideContent(props: { slide: EducationSlide }) {
  const { slide } = props;

  if (slide.id === "intro") {
    return <EducationIntroSlide marketplaceType="shifts" />;
  }

  if (slide.id === "upload-documents") {
    return <UploadDocumentsSlide slide={slide} />;
  }

  if (slide.id === "instant-book") {
    return <InstantBookSlide slide={slide} />;
  }

  if (slide.id === "instant-pay") {
    return <InstantPaySlide slide={slide} />;
  }

  // Default placeholder for other slides
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        paddingX: 5,
        textAlign: "center",
      }}
    >
      <Title variant="h3" component="h1">
        {slide.title}
      </Title>
      {slide.subtitle && <Text variant="body1">{slide.subtitle}</Text>}
    </Box>
  );
}

export function ShiftsEducationStepContent(props: OnboardingStepProps) {
  const { onNext } = props;
  const [currentIndex, setCurrentIndex] = useState(0);

  const isLastSlide = currentIndex === SLIDES.length - 1;

  useLogEffect(APP_V2_USER_EVENTS.ONBOARDING_SHIFTS_EDUCATION_PAGE_VIEWED);

  const handleContinue = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.ONBOARDING_STEP_COMPLETED, {
      stepCompleted: ONBOARDING_STAGES.SHIFTS_EDUCATION,
      slideIndex: currentIndex,
    });
    void onNext();
  }, [currentIndex, onNext]);

  const handleChangeStep = useCallback((step: number) => {
    setCurrentIndex(step);
  }, []);

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <SwipeableViewsWithDots
        activeStep={currentIndex}
        dotsPosition="bottom"
        dotsOffset="20%"
        onChangeStep={handleChangeStep}
      >
        {SLIDES.map((slide) => (
          <SlideContent key={slide.id} slide={slide} />
        ))}
      </SwipeableViewsWithDots>

      <PageFooterWrapper sx={{ visibility: isLastSlide ? "visible" : "hidden" }}>
        <Button fullWidth size="large" variant="contained" onClick={handleContinue}>
          Get started
        </Button>
      </PageFooterWrapper>
    </Box>
  );
}
